import { useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import offerPageURL, { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { findCheapestTourV2Variation } from 'lib/tours/tourUtils'
import { isTourV2Offer, isTourV2OfferSummary } from 'lib/offer/offerTypes'

const messages = defineMessages({
  experiencesWishlistName: {
    id: 'luZsGdKW13arcJ5-N_r8',
    defaultMessage: 'Experiences Wishlist',
    description: 'Default suggested name pre-filled when a user saves an Experience offer to a new wishlist',
  },
})

export interface WishlistDisplayFields {
  title: string
  imageIds: Array<string>
  subtitle?: string
  imageId?: string
  deeplinkUrl?: string
  locationName?: string
  suggestedWishlistName?: string
  latitude?: number
  longitude?: number
}

const EMPTY_IMAGE_IDS: Array<string> = []

function extractImageIds(
  images?: ReadonlyArray<{ id?: string }>,
): Array<string> {
  if (!images?.length) return EMPTY_IMAGE_IDS
  return images.reduce<Array<string>>((acc, image) => {
    if (image.id) acc.push(image.id)
    return acc
  }, [])
}

function getAddressCountryName(address: unknown): string | undefined {
  if (!address || typeof address !== 'object' || !('countryName' in address)) {
    return undefined
  }

  const { countryName } = address as { countryName?: unknown }

  return typeof countryName === 'string' ? countryName : undefined
}

function getHotelWishlistLocationName(
  offer: App.HotelOffer | App.HotelOfferSummary,
): string | undefined {
  const addressCountryName = getAddressCountryName(offer.property.address)

  return offer.locationSubheading ||
    offer.property.geoData?.country ||
    addressCountryName ||
    offer.location
}

function getBedbankWishlistLocationName(
  offer: App.BedbankOffer | App.BedbankOfferSummary,
): string | undefined {
  return getAddressCountryName(offer.property.address) ||
    offer.property.location
}

export function useHotelWishlistDisplayFields(
  offer: App.HotelOffer | App.HotelOfferSummary,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])
  const locationName = getHotelWishlistLocationName(offer)

  return useMemo(() => ({
    title: offer.property.name,
    imageIds,
    subtitle: offer.location,
    imageId: offer.image.id,
    deeplinkUrl: offerPageURL(offer),
    locationName,
    latitude: offer.property.latitude,
    longitude: offer.property.longitude,
  }), [imageIds, locationName, offer])
}

export function useBedbankWishlistDisplayFields(
  offer: App.BedbankOffer | App.BedbankOfferSummary,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])
  const locationName = getBedbankWishlistLocationName(offer)

  return useMemo(() => ({
    title: offer.property.name,
    imageIds,
    subtitle: offer.property.location,
    imageId: offer.image.id,
    deeplinkUrl: offerPageURL(offer),
    locationName,
    latitude: offer.property.latitude,
    longitude: offer.property.longitude,
  }), [imageIds, locationName, offer])
}

export function useRentalWishlistDisplayFields(
  offer: App.HotelOffer | App.HotelOfferSummary,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])
  const locationName = getHotelWishlistLocationName(offer)

  return useMemo(() => ({
    title: offer.property.name,
    imageIds,
    subtitle: offer.location,
    imageId: offer.image.id,
    deeplinkUrl: offerPageURL(offer),
    locationName,
    latitude: offer.property.latitude,
    longitude: offer.property.longitude,
  }), [imageIds, locationName, offer])
}

export function useTourWishlistDisplayFields(
  offer: App.TourOffer | App.TourOfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
  selectedVariation?: App.Tours.TourV2OfferVariation,
): WishlistDisplayFields {
  const variation = selectedVariation ??
    ((isTourV2Offer(offer) || isTourV2OfferSummary(offer))
      ? findCheapestTourV2Variation(offer)
      : undefined)

  const imageIds = useMemo(
    () => variation?.images ? extractImageIds(variation.images) : EMPTY_IMAGE_IDS,
    [variation?.images],
  )

  const subtitle = (isTourV2Offer(offer) || isTourV2OfferSummary(offer))
    ? offer.operatedBy
    : undefined

  return useMemo(() => ({
    title: offer.name,
    imageIds,
    subtitle,
    imageId: imageIds[0],
    deeplinkUrl: offerPageURL(offer),
    locationName: variation?.startLocation,
  }), [offer, imageIds, subtitle, variation?.startLocation])
}

export function useCruiseWishlistDisplayFields(
  offer: App.CruiseOffer,
): WishlistDisplayFields {
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.name,
    imageIds,
    subtitle: offer.ship.name,
    imageId: imageIds[0],
    deeplinkUrl: offerPageURL(offer),
    locationName: offer.departurePort,
  }), [offer, imageIds])
}

export function useExperienceWishlistDisplayFields(
  offer: App.ExperienceOffer,
): WishlistDisplayFields {
  const intl = useIntl()
  const imageIds = useMemo(() => extractImageIds(offer.images), [offer.images])

  return useMemo(() => ({
    title: offer.name,
    imageIds,
    subtitle: offer.location.name,
    imageId: imageIds[0],
    deeplinkUrl: getExperienceOfferPageURL(offer),
    locationName: offer.location.name,
    suggestedWishlistName: intl.formatMessage(messages.experiencesWishlistName),
    latitude: offer.location.latitude,
    longitude: offer.location.longitude,
  }), [intl, offer, imageIds])
}
